import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { ReactElement, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import {
  CreateNewPricingBundleInput,
  makeCreateNewPricingBundleSchema,
} from "@stw/yup-validators";

import {
  CreateNewPricingBundleMutation,
  CreateNewPricingBundleMutationVariables,
  GetActivePricingBundlesQuery,
  GetActiveSkusQuery,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import {
  RhfCurrencyField,
  RhfErrorBanner,
  RhfSwitchField,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import {
  CREATE_NEW_PRICING_BUNDLE,
  GET_ACTIVE_PRICING_BUNDLES,
  LIST_ALL_ACTIVE_PRICING_ASSIGNMENTS,
} from "~frontend/graphql/pricingBundle";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { compareSkuNames } from "../../organization/pricing/helpers";

type Sku = GetActiveSkusQuery["getActiveSkus"][0];
type Bundle = GetActivePricingBundlesQuery["getActivePricingBundles"][0];

const MILLICENT_DECIMAL_PLACES = 5;

// A blank price field resolves to null, which the backend treats as
// "use the SKU's rack price".
type FormValues = {
  name: string;
  isWeText: boolean;
  prices: Array<{ skuId: string; priceInMillicents: number | null }>;
};

export function NewPricingBundleDialog({
  open,
  onClose,
  skus,
  bundles,
}: {
  open: boolean;
  onClose: () => void;
  skus: Sku[];
  bundles: Bundle[];
}): ReactElement {
  const formatErrorMessage = useErrorMessageFormatter();

  const sortedSkus = useMemo(
    () => [...skus].sort((a, b) => compareSkuNames(a.name, b.name)),
    [skus]
  );

  const standardPricesBySkuId = useMemo<Record<string, number>>(() => {
    const standard = bundles.find(b => b.name === "Standard" && !b.isWeText);
    if (!standard) {
      return {};
    }
    return Object.fromEntries(
      standard.prices.map(p => [p.sku.id, p.priceInMillicents])
    );
  }, [bundles]);

  const initialFormValues = useMemo<FormValues>(
    () => ({
      name: "",
      isWeText: false,
      prices: sortedSkus.map(sku => ({
        skuId: sku.id,
        priceInMillicents: sku.rackPriceInMillicents,
      })),
    }),
    [sortedSkus]
  );

  const resolver = useMemo(
    () =>
      zodResolver(makeCreateNewPricingBundleSchema({ standardPricesBySkuId })),
    [standardPricesBySkuId]
  );

  const methods = useForm<CreateNewPricingBundleInput, any, FormValues>({
    resolver,
    defaultValues: initialFormValues,
    values: initialFormValues,
    mode: "onSubmit",
  });

  const { control, handleSubmit, reset, setError, formState, watch } = methods;
  const isWeText = watch("isWeText");

  const [createBundle] = useMutation<
    CreateNewPricingBundleMutation,
    CreateNewPricingBundleMutationVariables
  >(CREATE_NEW_PRICING_BUNDLE, {
    onErrorSnackbarMessage: "Failed to create pricing option",
    refetchQueries: [
      GET_ACTIVE_PRICING_BUNDLES,
      LIST_ALL_ACTIVE_PRICING_ASSIGNMENTS,
    ],
    awaitRefetchQueries: true,
  });

  function handleClose() {
    reset(initialFormValues);
    onClose();
  }

  async function onSubmit(values: FormValues) {
    const { errors } = await createBundle({
      variables: {
        input: {
          name: values.name,
          isWeText: values.isWeText,
          prices: values.prices.map(price => ({
            skuId: price.skuId,
            priceInMillicents: price.priceInMillicents,
          })),
        },
      },
    });

    if (errors) {
      setError("root", { message: formatErrorMessage(errors) });
      return;
    }

    handleClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle variant="h1" sx={{ pt: 3, pb: 1 }}>
        Add a Pricing Option
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 0 }}
          >
            <FlexBox flexDirection="column" gap={2}>
              <RhfErrorBanner control={control} />

              <RhfTextField
                control={control}
                name="name"
                label="Pricing Option Name"
                fullWidth
              />

              <FlexBox direction="column">
                <FlexBox
                  alignItems="center"
                  sx={{
                    px: 2,
                    height: "2.5em",
                    backgroundColor: palette.lightGray,
                  }}
                >
                  <Typography variant="body2Bold">Prices</Typography>
                </FlexBox>
                <TwoColumnDisplay
                  allRowSx={{
                    backgroundColor: palette.lightestGray,
                    px: 2,
                  }}
                  labelWidth="200px"
                  labelVariant="body2Bold"
                  contentVariant="body2"
                  rows={sortedSkus.map((sku, index) => ({
                    key: sku.id,
                    label: sku.name,
                    content: (
                      <RhfCurrencyField
                        control={control}
                        name={`prices.${index}.priceInMillicents` as const}
                        label=""
                        isSlim
                        decimalPlaces={MILLICENT_DECIMAL_PLACES}
                      />
                    ),
                  }))}
                  showTopBorder={false}
                  showBottomBorder={false}
                />
              </FlexBox>

              <RhfSwitchField
                control={control}
                name="isWeText"
                label={
                  isWeText
                    ? "This pricing option is for WeText clients"
                    : "This pricing option is for self serve (non-WeText) clients"
                }
              />
            </FlexBox>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={formState.isSubmitting}
              onClick={handleClose}
              color="secondary"
            >
              Close
            </Button>
            <Button
              disabled={formState.isSubmitting}
              loading={formState.isSubmitting}
              color="primary"
              variant="contained"
              type="submit"
            >
              Create Pricing Option
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}
